import React, { useCallback, useLayoutEffect } from 'react'
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom'

const STORAGE_KEY = '2pb-scroll-positions-v1'
function readPositions() {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') } catch { return {} }
}
function scrollSelector(element) {
  const parts = []
  while (element && element.tagName !== 'MAIN') {
    const siblings = [...element.parentElement.children].filter(node => node.tagName === element.tagName)
    parts.unshift(`${element.localName}:nth-of-type(${siblings.indexOf(element) + 1})`)
    element = element.parentElement
  }
  return `main > ${parts.join(' > ')}`
}
export function savePosition(key) {
  try {
    const positions = readPositions()
    delete positions[key]
    const containers = [...document.querySelectorAll('main *')]
      .filter(element => element.scrollTop || element.scrollLeft)
      .map(element => ({ selector: scrollSelector(element), x: element.scrollLeft, y: element.scrollTop }))
    positions[key] = { x: window.scrollX, y: window.scrollY, containers }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(Object.entries(positions).slice(-100))))
  } catch { /* Navigation works when browser storage is unavailable. */ }
}
function originOf(location) {
  savePosition(location.key)
  return { url: location.pathname + location.search + location.hash, key: location.key,
    index: window.history.state?.idx, state: location.state }
}

export function EntityLink({ state, onClick, ...props }) {
  const location = useLocation()
  if (!props.to) {
    const { to, ...spanProps } = props
    return <span {...spanProps} onClick={event => event.stopPropagation()} />
  }
  return <Link {...props} state={{ ...state, navigationOrigin: {
    url: location.pathname + location.search + location.hash, key: location.key,
    index: window.history.state?.idx, state: location.state,
  } }} onClick={event => {
    savePosition(location.key)
    // A parent row must not also navigate, especially for Cmd/Ctrl-clicks.
    event.stopPropagation()
    onClick?.(event)
  }} />
}

export function useEntityNavigate() {
  const navigate = useNavigate()
  const location = useLocation()
  return useCallback((to, options = {}) => {
    if (typeof to === 'number' || options.replace) return navigate(to, options)
    navigate(to, { ...options, state: { ...options.state, navigationOrigin: originOf(location) } })
  }, [navigate, location])
}

export function useBack(fallback) {
  const navigate = useNavigate()
  const location = useLocation()
  return useCallback(() => {
    const origin = location.state?.navigationOrigin
    if (!origin?.url?.startsWith('/') || origin.url.startsWith('//')) {
      return typeof fallback === 'function' ? fallback() : navigate(fallback)
    }
    // Direct/new-tab entries have no origin. Replacements and saves can move
    // the source away from the adjacent history entry; restore it explicitly.
    const index = window.history.state?.idx
    if (Number.isInteger(index) && index === origin.index + 1) return navigate(-1)
    navigate(origin.url, { replace: true, state: { ...origin.state, restoreScrollKey: origin.key } })
  }, [navigate, location, fallback])
}

/** Wait for asynchronous list rows before restoring a POP/reload position. */
export function NavigationScroll() {
  const location = useLocation()
  const type = useNavigationType()
  useLayoutEffect(() => {
    const oldRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    const key = location.state?.restoreScrollKey || location.key
    const target = (type === 'POP' || location.state?.restoreScrollKey) ? readPositions()[key] : null
    let restoring = Boolean(target)
    let observer
    let timer
    const stop = () => { restoring = false; observer?.disconnect(); clearTimeout(timer) }
    const restore = () => {
      if (!restoring) return
      const root = document.documentElement
      const containers = (target.containers || []).map(position => ({
        ...position, element: document.querySelector(position.selector),
      }))
      if (root.scrollHeight - window.innerHeight >= target.y && containers.every(({ element, x, y }) =>
        element && element.scrollHeight - element.clientHeight >= y && element.scrollWidth - element.clientWidth >= x)) {
        for (const { element, x, y } of containers) { element.scrollTop = y; element.scrollLeft = x }
        window.scrollTo(target.x, target.y)
        stop()
      }
    }
    if (target) {
      observer = new MutationObserver(restore)
      observer.observe(document.body, { childList: true, subtree: true, attributes: true })
      timer = setTimeout(stop, 15000)
      restore()
    } else window.scrollTo(0, 0)
    const save = () => { if (!restoring) savePosition(location.key) }
    const cancel = () => { stop(); save() }
    window.addEventListener('scroll', save, { passive: true, capture: true })
    window.addEventListener('pagehide', save)
    window.addEventListener('wheel', cancel, { passive: true })
    window.addEventListener('touchstart', cancel, { passive: true })
    return () => {
      stop()
      window.removeEventListener('scroll', save, true)
      window.removeEventListener('pagehide', save)
      window.removeEventListener('wheel', cancel)
      window.removeEventListener('touchstart', cancel)
      window.history.scrollRestoration = oldRestoration
    }
  }, [location.key])
  return null
}
